import { Box, Fade, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import MobileStepper from '@mui/material/MobileStepper'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { getCurrentMode } from '../../../../../store/redux/reducers/darkModeSlice'
import { Item } from '../../../../../store/types'
import { t } from 'i18next'

type CarouselProps = {
  title: string
  items: Item[]
}

const Carousel = ({ title, items }: CarouselProps) => {
  const [activeStep, setActiveStep] = useState(0)

  const mode = useSelector(getCurrentMode)

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const carouselType = items[0]?.key.replace(/-\d+$/, '')

  const getHeight = () => {
    switch (carouselType) {
      case 'carousel-schedule-info-plan':
        return isDownSm ? '350px' : '440px'
      case 'carousel-schedule-info-modify-many':
        return isDownSm ? '442px' : '444px'
      case 'carousel-schedule-info-tracked':
        return isDownSm ? '471px' : '580px'
      case 'carousel-worklog-info-cost':
        return isDownSm ? '405px' : '560px'
      case 'carousel-worklog-info-workRecord':
        return isDownSm ? '350px' : '430px'
      case 'carousel-import-info-workRecord':
        return isDownSm ? '450px' : '560px'
      case 'carousel-schedule-info-delete':
      case 'carousel-schedule-info-history':
        return 'auto'
      default:
        return '100px'
    }
  }

  const activeItem = items[activeStep]
  const isImageAvailable = activeItem?.imageLight && activeItem?.imageDark

  return (
    <Stack maxWidth="100%">
      <Typography variant="h6" color="text.highEmphasis" component="h2">
        {title}
      </Typography>

      <Stack sx={{ height: getHeight(), position: 'relative', mb: 2 }}>
        <Box sx={{ mb: 2, zIndex: 2 }}>
          {items.map((item, index) => (
            <Fade
              in={activeStep === index}
              key={`text-${item.key}`}
              style={{ display: activeStep === index ? 'block' : 'none' }}
            >
              <Stack gap={1}>
                <Typography variant="caption" color="text.mediumEmphasis" fontWeight={300} component="span">
                  {item.subtitle}
                </Typography>
                <Typography component="div" color="text.base">
                  {item.description}
                </Typography>
              </Stack>
            </Fade>
          ))}
        </Box>

        <Box sx={{ position: 'relative', flexGrow: 1 }}>
          {isImageAvailable && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            />
          )}

          {items.map((item, index) => {
            const isCurrentImageAvailable = item.imageLight && item.imageDark

            return isCurrentImageAvailable ? (
              <Fade
                in={activeStep === index}
                key={`img-${item.key}`}
                style={{
                  display: activeStep === index ? 'flex' : 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    p: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={mode === 'light' ? item.imageLight : item.imageDark}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                    alt={t('components.infoSidebar.alternativeText')}
                  />
                </Box>
              </Fade>
            ) : null
          })}
        </Box>
      </Stack>

      {items.length > 1 && (
        <Stack direction="row" justifyContent="center" pb={2}>
          <MobileStepper
            variant="dots"
            steps={items.length}
            position="static"
            activeStep={activeStep}
            sx={{ bgcolor: 'transparent' }}
            nextButton={
              <Tooltip title={t('components.infoSidebar.next')}>
                <span>
                  <IconButton
                    aria-label={t('components.infoSidebar.next')}
                    size="small"
                    onClick={handleNext}
                    disabled={activeStep === items.length - 1}
                    sx={{ ml: 2 }}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                </span>
              </Tooltip>
            }
            backButton={
              <Tooltip title={t('components.infoSidebar.previous')}>
                <span>
                  <IconButton
                    aria-label={t('components.infoSidebar.previous')}
                    size="small"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    sx={{ mr: 2 }}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                </span>
              </Tooltip>
            }
          />
        </Stack>
      )}
    </Stack>
  )
}

export default Carousel
